/**
 * InvestmentTable — sortable financial table showing all budget-linked nodes.
 *
 * Columns: Priority#, Name, Initiative, Phase, Status, iR&D, FR&D, Total Inv,
 * Est Revenue, Est R&D (SP), FTEs, TAM, SAM, Break Even, ROI, NPV, MVP.
 * Totals row at bottom. TAM/SAM inline editing. AI-suggested indicator.
 */

import { useState, useMemo, useCallback } from 'react';
import { NodeModal } from '../editors/NodeModal';
import { FinancialDetailModal } from './FinancialDetailModal';
import { ArrowUpDown, ArrowUp, ArrowDown, Sparkles, Check, X } from 'lucide-react';
import type { AdjacencyNode } from '../../types/adjacency';
import { INITIATIVE_COLORS } from '../../utils/map-colors';
import { PHASE_COLORS } from '../../utils/view-colors';
import { computeFinancialMetrics, formatCurrency, formatCompactCurrency } from '../../utils/financial-calculations';
import type { BudgetProject } from '../../utils/budget-import';
import budgetData from '../../data/budget-2026.json';
import { useAdjacencyStore } from '../../store/adjacency-store';

const budgetProjects = budgetData.projects as BudgetProject[];

type SortKey =
  | 'budgetPriority' | 'name' | 'initiative' | 'phase' | 'status'
  | 'iRnD' | 'fRnD' | 'totalInv' | 'estRevenue' | 'estRnD'
  | 'fteAllocation' | 'tam' | 'sam' | 'breakEven' | 'roi' | 'npv' | 'isMvp';

type SortDir = 'asc' | 'desc' | null;

interface Props {
  nodes: AdjacencyNode[];
  discountRate: number;
  periodYears: number;
  fteAllocations: Map<string, number>;
}

export function InvestmentTable({ nodes, discountRate, periodYears, fteAllocations }: Props) {
  const allNodes = useAdjacencyStore((s) => s.nodes);
  const updateNode = useAdjacencyStore((s) => s.updateNode);
  const deleteNode = useAdjacencyStore((s) => s.deleteNode);
  const duplicateNode = useAdjacencyStore((s) => s.duplicateNode);
  const createParentChildLink = useAdjacencyStore((s) => s.createParentChildLink);
  const createRelatedLink = useAdjacencyStore((s) => s.createRelatedLink);
  const removeRelationship = useAdjacencyStore((s) => s.removeRelationship);
  const convertEdgeType = useAdjacencyStore((s) => s.convertEdgeType);
  const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
  const [financeModal, setFinanceModal] = useState<{ nodeId: string; mode: 'npv' | 'roi' | 'breakeven' } | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('budgetPriority');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [editingCell, setEditingCell] = useState<{ nodeId: string; field: 'tam' | 'sam' } | null>(null);
  const [editValue, setEditValue] = useState('');

  const budgetMap = useMemo(() => {
    const m = new Map<string, BudgetProject>();
    for (const p of budgetProjects) m.set(p.project, p);
    return m;
  }, []);

  // Compute metrics for each node
  const rows = useMemo(() => {
    return nodes.map((node) => {
      const fm = computeFinancialMetrics(node, discountRate, periodYears);
      const bp = node.budgetProjectName ? budgetMap.get(node.budgetProjectName) : null;
      return { node, fm, budgetPriority: bp?.budgetPriority ?? 999 };
    });
  }, [nodes, discountRate, periodYears, budgetMap]);

  // Sort rows
  const sorted = useMemo(() => {
    if (!sortDir) return rows;
    const mult = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (va === vb) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return va < vb ? -1 * mult : 1 * mult;
    });
  }, [rows, sortKey, sortDir]);

  // Totals
  const totals = useMemo(() => {
    let iRnD = 0, fRnD = 0, totalInv = 0, estRevenue = 0, estRnD = 0, fte = 0, tam = 0, sam = 0;
    let roiSum = 0, roiCount = 0, npvSum = 0, npvCount = 0;
    for (const r of rows) {
      iRnD += r.node.iRnD ?? 0;
      fRnD += r.node.fRnD ?? 0;
      totalInv += r.fm.totalInvestment;
      estRevenue += r.node.estRevenue ?? 0;
      estRnD += r.node.estRnD ?? 0;
      fte += fteAllocations.get(r.node.id) ?? r.node.fteAllocation ?? 0;
      tam += r.node.tam ?? 0;
      sam += r.node.sam ?? 0;
      if (r.fm.roi != null) { roiSum += r.fm.roi; roiCount++; }
      if (r.fm.npv != null) { npvSum += r.fm.npv; npvCount++; }
    }
    return {
      iRnD, fRnD, totalInv, estRevenue, estRnD, fte, tam, sam,
      avgRoi: roiCount > 0 ? roiSum / roiCount : null,
      totalNpv: npvCount > 0 ? npvSum : null,
    };
  }, [rows]);

  const handleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey]);

  const startEdit = (nodeId: string, field: 'tam' | 'sam', currentValue: number | null) => {
    setEditingCell({ nodeId, field });
    setEditValue(currentValue != null ? String(currentValue) : '');
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const num = parseFloat(editValue);
    const val = isNaN(num) ? null : num;
    updateNode(editingCell.nodeId, { [editingCell.field]: val });
    setEditingCell(null);
  };

  const cancelEdit = () => setEditingCell(null);

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col || !sortDir) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  return (
    <div className="glass-panel rounded-lg">
      <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <table className="text-[11px] border-collapse" style={{ minWidth: '1100px' }}>
          <thead>
            <tr style={{ background: 'var(--table-header-bg)' }}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="px-2 py-2 font-medium whitespace-nowrap cursor-pointer select-none"
                  style={{
                    color: 'var(--text-muted)',
                    textAlign: col.align ?? 'left',
                    borderBottom: '1px solid var(--table-border)',
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    <SortIcon col={col.key} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr
                key={row.node.id}
                onDoubleClick={() => setDetailNodeId(row.node.id)}
                className="cursor-pointer"
                style={{
                  background: i % 2 === 0 ? 'transparent' : 'var(--table-row-alt-bg)',
                  borderBottom: '1px solid var(--table-border)',
                }}
              >
                {/* Budget Priority */}
                <td className="px-2 py-1.5 tabular-nums text-center" style={{ color: 'var(--text-muted)' }}>
                  {row.budgetPriority < 999 ? row.budgetPriority : '—'}
                </td>
                {/* Name */}
                <td className="px-2 py-1.5 max-w-[180px] truncate" style={{ color: 'var(--text-primary)' }}>
                  {row.node.name}
                </td>
                {/* Initiative */}
                <td className="px-2 py-1.5">
                  <span
                    className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium"
                    style={{
                      background: (INITIATIVE_COLORS[row.node.strategicInitiative] ?? '#6b7280') + '25',
                      color: INITIATIVE_COLORS[row.node.strategicInitiative] ?? '#6b7280',
                    }}
                  >
                    {row.node.strategicInitiative}
                  </span>
                </td>
                {/* Phase */}
                <td className="px-2 py-1.5" style={{ color: PHASE_COLORS[row.node.phase ?? ''] ?? 'var(--text-muted)' }}>
                  {row.node.phase ?? '—'}
                </td>
                {/* Status */}
                <td className="px-2 py-1.5" style={{ color: 'var(--text-secondary)' }}>
                  {row.node.status ?? '—'}
                </td>
                {/* iR&D */}
                <td className="px-2 py-1.5 tabular-nums text-right" style={{ color: 'var(--text-secondary)' }}>
                  {row.node.iRnD != null ? formatCurrency(row.node.iRnD) : '—'}
                </td>
                {/* FR&D */}
                <td className="px-2 py-1.5 tabular-nums text-right" style={{ color: 'var(--text-secondary)' }}>
                  {row.node.fRnD != null ? formatCurrency(row.node.fRnD) : '—'}
                </td>
                {/* Total Investment */}
                <td className="px-2 py-1.5 tabular-nums text-right font-medium" style={{ color: 'var(--text-primary)' }}>
                  {row.fm.totalInvestment > 0 ? formatCurrency(row.fm.totalInvestment) : '—'}
                </td>
                {/* Est Revenue */}
                <td className="px-2 py-1.5 tabular-nums text-right" style={{ color: '#22c55e' }}>
                  {row.node.estRevenue != null ? formatCurrency(row.node.estRevenue) : '—'}
                </td>
                {/* Est R&D (SP) */}
                <td className="px-2 py-1.5 tabular-nums text-right" style={{ color: 'var(--text-secondary)' }}>
                  {row.node.estRnD != null ? formatCurrency(row.node.estRnD) : '—'}
                </td>
                {/* FTEs */}
                <td className="px-2 py-1.5 tabular-nums text-right" style={{ color: 'var(--text-secondary)' }}>
                  {(() => { const fte = fteAllocations.get(row.node.id) ?? row.node.fteAllocation; return fte != null ? fte.toFixed(1) : '—'; })()}
                </td>
                {/* TAM (editable) */}
                <td className="px-2 py-1.5 tabular-nums text-right" style={{ color: 'var(--text-secondary)' }}>
                  {editingCell?.nodeId === row.node.id && editingCell.field === 'tam' ? (
                    <span className="inline-flex items-center gap-0.5">
                      <input
                        type="number"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
                        className="w-20 px-1 py-0.5 rounded text-right text-[11px]"
                        style={{ background: 'var(--modal-input-bg)', color: 'var(--text-primary)', border: '1px solid rgba(59,130,246,0.5)' }}
                      />
                      <button onClick={commitEdit}><Check size={10} color="#22c55e" /></button>
                      <button onClick={cancelEdit}><X size={10} color="#ef4444" /></button>
                    </span>
                  ) : (
                    <span
                      className="cursor-pointer hover:underline inline-flex items-center gap-0.5"
                      onClick={() => startEdit(row.node.id, 'tam', row.node.tam)}
                    >
                      {row.node.tam != null ? formatCompactCurrency(row.node.tam) : '—'}
                      {row.node.tamAiSuggested && <Sparkles size={10} color="#d97706" />}
                    </span>
                  )}
                </td>
                {/* SAM (editable) */}
                <td className="px-2 py-1.5 tabular-nums text-right" style={{ color: 'var(--text-secondary)' }}>
                  {editingCell?.nodeId === row.node.id && editingCell.field === 'sam' ? (
                    <span className="inline-flex items-center gap-0.5">
                      <input
                        type="number"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
                        className="w-20 px-1 py-0.5 rounded text-right text-[11px]"
                        style={{ background: 'var(--modal-input-bg)', color: 'var(--text-primary)', border: '1px solid rgba(59,130,246,0.5)' }}
                      />
                      <button onClick={commitEdit}><Check size={10} color="#22c55e" /></button>
                      <button onClick={cancelEdit}><X size={10} color="#ef4444" /></button>
                    </span>
                  ) : (
                    <span
                      className="cursor-pointer hover:underline inline-flex items-center gap-0.5"
                      onClick={() => startEdit(row.node.id, 'sam', row.node.sam)}
                    >
                      {row.node.sam != null ? formatCompactCurrency(row.node.sam) : '—'}
                      {row.node.samAiSuggested && <Sparkles size={10} color="#d97706" />}
                    </span>
                  )}
                </td>
                {/* Break Even — clickable */}
                <td
                  className="px-2 py-1.5 tabular-nums text-right cursor-pointer hover:underline"
                  style={{ color: 'var(--text-secondary)' }}
                  onClick={(e) => { e.stopPropagation(); setFinanceModal({ nodeId: row.node.id, mode: 'breakeven' }); }}
                >
                  {row.fm.breakEvenYears != null ? `${row.fm.breakEvenYears.toFixed(1)} yr` : '—'}
                </td>
                {/* ROI — clickable */}
                <td
                  className="px-2 py-1.5 tabular-nums text-right font-medium cursor-pointer hover:underline"
                  style={{ color: row.fm.roi != null ? (row.fm.roi >= 0 ? '#22c55e' : '#ef4444') : 'var(--text-muted)' }}
                  onClick={(e) => { e.stopPropagation(); setFinanceModal({ nodeId: row.node.id, mode: 'roi' }); }}
                >
                  {row.fm.roi != null ? `${row.fm.roi.toFixed(1)}%` : '—'}
                </td>
                {/* NPV — clickable */}
                <td
                  className="px-2 py-1.5 tabular-nums text-right cursor-pointer hover:underline"
                  style={{ color: row.fm.npv != null ? (row.fm.npv >= 0 ? '#22c55e' : '#ef4444') : 'var(--text-muted)' }}
                  onClick={(e) => { e.stopPropagation(); setFinanceModal({ nodeId: row.node.id, mode: 'npv' }); }}
                >
                  {row.fm.npv != null ? formatCompactCurrency(row.fm.npv) : '—'}
                </td>
                {/* MVP */}
                <td className="px-2 py-1.5 text-center" style={{ color: row.node.isMvp ? '#d97706' : 'var(--text-dim)' }}>
                  {row.node.isMvp ? '✓' : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          {/* Totals row */}
          <tfoot>
            <tr style={{ background: 'rgba(59,130,246,0.08)', borderTop: '2px solid rgba(59,130,246,0.2)' }}>
              <td className="px-2 py-2 font-bold" style={{ color: 'var(--text-primary)' }} colSpan={2}>
                Totals ({rows.length} projects)
              </td>
              <td colSpan={3} />
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                {formatCurrency(totals.iRnD)}
              </td>
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                {formatCurrency(totals.fRnD)}
              </td>
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                {formatCurrency(totals.totalInv)}
              </td>
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: '#22c55e' }}>
                {totals.estRevenue > 0 ? formatCurrency(totals.estRevenue) : '—'}
              </td>
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                {totals.estRnD > 0 ? formatCurrency(totals.estRnD) : '—'}
              </td>
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                {totals.fte > 0 ? totals.fte.toFixed(1) : '—'}
              </td>
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                {totals.tam > 0 ? formatCompactCurrency(totals.tam) : '—'}
              </td>
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                {totals.sam > 0 ? formatCompactCurrency(totals.sam) : '—'}
              </td>
              <td />
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                {totals.avgRoi != null ? `${totals.avgRoi.toFixed(1)}%` : '—'}
              </td>
              <td className="px-2 py-2 tabular-nums text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                {totals.totalNpv != null ? formatCompactCurrency(totals.totalNpv) : '—'}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* FinancialDetailModal — click Break-Even/ROI/NPV cells */}
      {financeModal && (() => {
        const fmNode = allNodes.find(n => n.id === financeModal.nodeId);
        if (!fmNode) return null;
        return (
          <FinancialDetailModal
            node={fmNode}
            mode={financeModal.mode}
            discountRate={discountRate}
            periodYears={periodYears}
            onClose={() => setFinanceModal(null)}
          />
        );
      })()}

      {/* NodeModal — portals to document.body */}
      {detailNodeId && (() => {
        const detailNode = allNodes.find(n => n.id === detailNodeId);
        if (!detailNode) return null;
        return (
          <NodeModal node={detailNode} allNodes={allNodes}
            onClose={() => setDetailNodeId(null)}
            onSave={(id, changes) => { void updateNode(id, changes); }}
            onDelete={(id) => { void deleteNode(id); setDetailNodeId(null); }}
            onDuplicate={(id) => { void duplicateNode(id); }}
            onAddParent={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
            onAddChild={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
            onAddRelated={(a, b) => { void createRelatedLink(a, b); }}
            onRemoveRelationship={(a, b) => { void removeRelationship(a, b); }}
            onConvertEdgeType={(a, b, toType, parentId) => { void convertEdgeType(a, b, toType, parentId); }}
          />
        );
      })()}
    </div>
  );
}

// ── Column definitions ────────────────────────────────────────────────

const COLUMNS: { key: SortKey; label: string; align?: 'left' | 'center' | 'right' }[] = [
  { key: 'budgetPriority', label: '#', align: 'center' },
  { key: 'name', label: 'Name' },
  { key: 'initiative', label: 'Initiative' },
  { key: 'phase', label: 'Phase' },
  { key: 'status', label: 'Status' },
  { key: 'iRnD', label: 'iR&D', align: 'right' },
  { key: 'fRnD', label: 'FR&D', align: 'right' },
  { key: 'totalInv', label: 'Total Inv.', align: 'right' },
  { key: 'estRevenue', label: 'Est Revenue', align: 'right' },
  { key: 'estRnD', label: 'Est R&D (SP)', align: 'right' },
  { key: 'fteAllocation', label: 'FTEs', align: 'right' },
  { key: 'tam', label: 'TAM', align: 'right' },
  { key: 'sam', label: 'SAM', align: 'right' },
  { key: 'breakEven', label: 'Break Even', align: 'right' },
  { key: 'roi', label: 'ROI', align: 'right' },
  { key: 'npv', label: 'NPV', align: 'right' },
  { key: 'isMvp', label: 'MVP', align: 'center' },
];

// ── Sort value extraction ─────────────────────────────────────────────

function getSortValue(
  row: { node: AdjacencyNode; fm: ReturnType<typeof computeFinancialMetrics>; budgetPriority: number },
  key: SortKey,
): string | number | null {
  switch (key) {
    case 'budgetPriority': return row.budgetPriority;
    case 'name': return row.node.name.toLowerCase();
    case 'initiative': return row.node.strategicInitiative;
    case 'phase': return row.node.phase ?? '';
    case 'status': return row.node.status ?? '';
    case 'iRnD': return row.node.iRnD;
    case 'fRnD': return row.node.fRnD;
    case 'totalInv': return row.fm.totalInvestment || null;
    case 'estRevenue': return row.node.estRevenue;
    case 'estRnD': return row.node.estRnD;
    case 'fteAllocation': return row.node.fteAllocation;
    case 'tam': return row.node.tam;
    case 'sam': return row.node.sam;
    case 'breakEven': return row.fm.breakEvenYears;
    case 'roi': return row.fm.roi;
    case 'npv': return row.fm.npv;
    case 'isMvp': return row.node.isMvp ? 1 : 0;
  }
}
